import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import PageBanner from '@/components/site/PageBanner';
import SEO from '@/components/site/SEO';
import Reveal from '@/components/site/Reveal';
import { CheckCircle2, FileText, ClipboardList } from 'lucide-react';

const LoanPage = ({ title, tagline, features = [], docs = [], eligibility = [], seo = {} }) => {
  const [form, setForm] = useState({ name: '', phone: '', email: '', city: '', amount: '' });
  const [submitted, setSubmitted] = useState(false);

  const submit = (e) => {
    e.preventDefault();
    const list = JSON.parse(localStorage.getItem('finirio_leads') || '[]');
    list.push({ product: title, ...form, ts: Date.now() });
    localStorage.setItem('finirio_leads', JSON.stringify(list));
    setSubmitted(true);
  };

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FinancialProduct',
    name: title,
    provider: { '@type': 'Organization', name: 'Finirio', url: 'https://finirio.com' },
    description: seo.description || tagline,
    areaServed: 'IN',
    ...(seo.rate && { interestRate: seo.rate }),
  };

  const inputCls =
    'w-full h-11 px-3.5 border border-slate-200 rounded-lg bg-white outline-none transition focus:border-[#1e73be] focus:ring-2 focus:ring-[#1e73be]/20';

  return (
    <main>
      <SEO
        title={seo.title || `${title} | Finirio`}
        description={seo.description || tagline}
        path={seo.path || '/'}
        keywords={seo.keywords}
        jsonLd={jsonLd}
        jsonLdId="loan-jsonld"
      />
      <PageBanner title={title} crumbs={[ 'Services', title ]} />
      <section className="py-16 bg-white">
        <div className="max-w-[1240px] mx-auto px-4 lg:px-6 grid grid-cols-1 lg:grid-cols-3 gap-10">
          <div className="lg:col-span-2">
            <Reveal>
              <p className="text-slate-600 text-[16px] leading-relaxed mb-8">{tagline}</p>
            </Reveal>

            <Reveal>
              <h3 className="font-heading text-[22px] font-bold text-[#0b2545] mb-4 flex items-center gap-2.5">
                <span className="w-9 h-9 rounded-lg grid place-items-center flex-none" style={{ backgroundColor: '#e7f6f2' }}>
                  <CheckCircle2 className="w-5 h-5" style={{ color: '#2ea690' }} />
                </span>
                Key Features
              </h3>
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-10">
                {features.map((f, i) => (
                  <li key={i} className="flex items-start gap-3 text-slate-700 bg-[#fbf9f4] border border-[#0b2545]/6 rounded-lg px-4 py-3">
                    <span className="mt-2 w-1.5 h-1.5 rounded-full flex-none" style={{ backgroundColor: '#c9a24b' }} />
                    <span className="text-[15px]">{f}</span>
                  </li>
                ))}
              </ul>
            </Reveal>

            <Reveal>
              <h3 className="font-heading text-[22px] font-bold text-[#0b2545] mb-4 flex items-center gap-2.5">
                <span className="w-9 h-9 rounded-lg grid place-items-center flex-none" style={{ backgroundColor: '#eaf3fb' }}>
                  <ClipboardList className="w-5 h-5" style={{ color: '#1e73be' }} />
                </span>
                Eligibility
              </h3>
              <ul className="list-disc pl-6 space-y-1.5 text-slate-700 text-[15px] mb-10">
                {eligibility.map((e, i) => <li key={i}>{e}</li>)}
              </ul>
            </Reveal>

            <Reveal>
              <h3 className="font-heading text-[22px] font-bold text-[#0b2545] mb-4 flex items-center gap-2.5">
                <span className="w-9 h-9 rounded-lg grid place-items-center flex-none" style={{ backgroundColor: '#faf3e3' }}>
                  <FileText className="w-5 h-5" style={{ color: '#a9812f' }} />
                </span>
                Documents Required
              </h3>
              <ul className="list-disc pl-6 space-y-1.5 text-slate-700 text-[15px]">
                {docs.map((d, i) => <li key={i}>{d}</li>)}
              </ul>
            </Reveal>

            <Reveal>
              <div className="mt-10 rounded-xl border border-[#c9a24b]/30 bg-[#fdfbf5] p-6 shadow-[0_14px_30px_-20px_rgba(11,37,69,.2)]">
                <h3 className="font-heading font-bold text-[#0b2545] text-[17px] mb-1.5">
                  Why apply through Finirio instead of the bank directly?
                </h3>
                <p className="text-slate-700 text-[14px] leading-relaxed">
                  Finirio&apos;s bulk monthly business with 20+ banks gets you a lower negotiated interest rate, and
                  charges of about ₹30,000–₹45,000 on a ₹1 Crore file instead of ~₹60,000 — with a free dedicated
                  relationship manager and zero customer fee.{' '}
                  <Link to="/why-finirio" className="text-[#1e73be] font-semibold underline">See how it works</Link>
                  {' '}·{' '}
                  <Link to="/home-loan-guide-mumbai" className="text-[#1e73be] font-semibold underline">Home loan guide</Link>
                </p>
              </div>
            </Reveal>
          </div>

          <div className="card-lux p-6 h-fit sticky top-24">
            <h3 className="font-heading text-[21px] font-bold text-[#0b2545] mb-1 text-center">Apply Now</h3>
            <span className="block h-[2px] w-12 mx-auto mb-5 rounded-full" style={{ background: 'linear-gradient(90deg,#c9a24b,#e8c887)' }} />
            {submitted ? (
              <div className="text-center py-6">
                <CheckCircle2 className="w-10 h-10 mx-auto mb-3" style={{ color: '#2ea690' }} />
                <p className="text-slate-700">Thanks! Our advisor will call you shortly.</p>
              </div>
            ) : (
              <form className="space-y-3" onSubmit={submit}>
                <input required placeholder="Full Name" value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className={inputCls} />
                <input required placeholder="Mobile Number" value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  className={inputCls} />
                <input type="email" placeholder="Email" value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className={inputCls} />
                <input placeholder="City" value={form.city}
                  onChange={(e) => setForm({ ...form, city: e.target.value })}
                  className={inputCls} />
                <input placeholder="Loan Amount" value={form.amount}
                  onChange={(e) => setForm({ ...form, amount: e.target.value })}
                  className={inputCls} />
                <button type="submit" className="btn-lux w-full justify-center !py-3">
                  Submit
                </button>
              </form>
            )}
          </div>
        </div>
      </section>
    </main>
  );
};

export default LoanPage;
